import {
    useCallback, useEffect, useId, useRef,
} from 'react';
import { useStore } from 'react-redux';
import {
    type HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel,
} from '@microsoft/signalr';
import { useDispatch, useInstanceSelector } from '@s20/hooks/redux.hooks';
import { getHubByUrl } from '@s20/selectors/features/signalr.selectors';
import {
    addHub, addToHub, removeHub, replaceHub, updateHubState,
} from '@s20/slices/features/signalr.slice';
import type { RootState } from '@s20/store';

const webLockAPISupported = (): boolean => typeof navigator !== 'undefined' && Boolean(navigator.locks) && Boolean(Promise.withResolvers);

/**
 * Generates a held web lock that can be used to ensure a tab doesn't sleep
 * Should be used in conjunction with useSignalR to ensure the connection doesn't fail
 */
export const useRunningWebLock = (lockName: string, options?: LockOptions) => {
    const resolversRef = useRef<PromiseWithResolvers<unknown> | null>(null);
    const optionsRef = useRef(options);

    useEffect(() => {
        optionsRef.current = options;
    });

    const requestLock = useCallback(async (name: string, resolvers: PromiseWithResolvers<unknown>) => {
        try {
            await navigator.locks.request(
                name,
                optionsRef.current || { mode: 'shared' },
                () => resolvers.promise,
            );
        } catch (e) {
            console.log('Error requesting web lock', { e });
        }
    }, []);

    useEffect(() => {
        if (webLockAPISupported()) {
            if (!resolversRef.current) {
                resolversRef.current = Promise.withResolvers();
            }

            void(requestLock(lockName, resolversRef.current));
        }

        return () => {
            if (resolversRef.current) {
                resolversRef.current.resolve(true);
                resolversRef.current = null;
            }
        };
    }, [lockName]);

    return undefined;
};

export type HubLifecycleEvent = 'hub-close' | 'hub-reconnecting' | 'hub-reconnected' | 'hub-start-failed' | 'hub-restarted';

interface SignalROptions {
    readonly onReconnect?: (hub: HubConnection, connectionId?: string | undefined) => void;
    readonly onLifecycle?: (event: HubLifecycleEvent, info: { connectionId?: string | null; error?: Error }) => void;
}

/** SignalR's default delays, then every 30 seconds for as long as the page is open: a hub never gives up. */
const RECONNECT_DELAYS_MS = [0, 2000, 10000, 30000];

const retryForever = {
    nextRetryDelayInMilliseconds: ({ previousRetryCount }: { previousRetryCount: number }) => (
        RECONNECT_DELAYS_MS[Math.min(previousRetryCount, RECONNECT_DELAYS_MS.length - 1)]
    ),
};

const RETRY_START_EVENTS = ['online', 'focus', 'visibilitychange', 'pointerdown'] as const;

/** Runs `retry` once, on the next sign that the user or the network is back. */
const onNextActivity = (retry: () => void) => {
    const handler = () => {
        if (document.visibilityState === 'hidden') return;

        RETRY_START_EVENTS.forEach((e) => window.removeEventListener(e, handler));
        retry();
    };

    RETRY_START_EVENTS.forEach((e) => window.addEventListener(e, handler));
};

/**
 * Generates and manages a SignalR hub that reconnects automatically. A connection that closes while the hub is still
 * in use is replaced, and one that fails to start tries again on the next user or network activity.
 */
export const useSignalR = (hubUrl: string, options?: SignalROptions): HubConnection | null => {
    const uniqueId = useId();

    const hub = useInstanceSelector(getHubByUrl, hubUrl);
    const dispatch = useDispatch();
    const store = useStore<RootState>();

    const optionsRef = useRef(options);

    useEffect(() => {
        optionsRef.current = options;
    });

    useEffect(() => {
        if (!hub) {
            const isCurrent = (connection: HubConnection) => getHubByUrl(store.getState(), hubUrl)?.hub === connection;

            const start = async (connection: HubConnection) => {
                try {
                    await connection.start();

                    if (isCurrent(connection)) {
                        dispatch(updateHubState({ id: hubUrl, hubState: HubConnectionState.Connected }));
                    }
                } catch (e) {
                    optionsRef.current?.onLifecycle?.('hub-start-failed', { error: e as Error });
                    onNextActivity(() => {
                        if (isCurrent(connection) && connection.state === HubConnectionState.Disconnected) {
                            void start(connection);
                        }
                    });
                }
            };

            const connect = (): HubConnection => {
                const connection = new HubConnectionBuilder()
                    .withUrl(hubUrl)
                    .configureLogging(LogLevel.Error)
                    .withAutomaticReconnect(retryForever)
                    .withStatefulReconnect()
                    .build();

                connection.onclose((e) => {
                    optionsRef.current?.onLifecycle?.('hub-close', { connectionId: connection.connectionId, error: e });

                    // removeHub stops the connection after taking it out of the store, so a connection that is still
                    // current closed on its own and the hub is still in use.
                    if (isCurrent(connection)) {
                        dispatch(replaceHub({ id: hubUrl, hub: connect() }));
                        optionsRef.current?.onLifecycle?.('hub-restarted', {});
                    }
                });

                connection.onreconnected((connectionId) => {
                    dispatch(updateHubState({ id: hubUrl, hubState: HubConnectionState.Connected }));
                    optionsRef.current?.onLifecycle?.('hub-reconnected', { connectionId });

                    if (optionsRef.current?.onReconnect) {
                        optionsRef.current.onReconnect(connection, connectionId);
                    }
                });

                connection.onreconnecting((error) => {
                    optionsRef.current?.onLifecycle?.('hub-reconnecting', { connectionId: connection.connectionId, error });
                    dispatch(updateHubState({ id: hubUrl, hubState: HubConnectionState.Reconnecting }));
                });

                void start(connection);

                return connection;
            };

            dispatch(addHub({
                id: hubUrl,
                uuid: uniqueId,
                hub: connect(),
            }));
        } else if (!hub.uuids.includes(uniqueId)) {
            dispatch(addToHub({
                id: hubUrl,
                uuid: uniqueId,
            }));
        }

        return () => {
            dispatch(removeHub(uniqueId));
        };
    }, [hubUrl]);

    if (hub?.hubState === HubConnectionState.Connected && hub?.hub) {
        return hub?.hub;
    }

    return null;
};
